import { z } from "zod";
import { bookGenerationBudgetV1Schema, bookGenerationCostEvidenceSourceSchema, bookGenerationSourceBindingV1Schema, } from "./book-generation.js";
import { comicProductionStageV2Schema } from "./comic-production.js";
import { recipeProductionStageV1Schema } from "./recipe-production.js";
import { ulidSchema } from "./schemas.js";
import { recipeContentV1Schema } from "./studio.js";
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const productBookProductionFamilySchema = z.enum(["COMIC_CORE", "RECIPE"]);
export const productBookProductionStageSchema = z.union([
    comicProductionStageV2Schema,
    recipeProductionStageV1Schema,
]);
export const productBookProductionCommandV1Schema = z
    .object({
    version: z.literal(1),
    productionFamily: productBookProductionFamilySchema,
    stage: productBookProductionStageSchema,
    expectedBookRevision: z.number().int().nonnegative(),
    expectedProductionRevision: z.number().int().nonnegative(),
})
    .strict()
    .superRefine((command, context) => {
    const stageBelongsToFamily = command.productionFamily === "COMIC_CORE"
        ? comicProductionStageV2Schema.safeParse(command.stage).success
        : recipeProductionStageV1Schema.safeParse(command.stage).success;
    if (!stageBelongsToFamily)
        context.addIssue({
            code: "custom",
            path: ["stage"],
            message: "The requested stage does not belong to this production family.",
        });
});
export const productBookProductionJobStateSchema = z.enum([
    "QUEUED",
    "RUNNING",
    "SUCCEEDED",
    "NEEDS_ATTENTION",
    "BLOCKED",
    "CANCELLED",
]);
export const productBookProductionExecutionV1Schema = z.discriminatedUnion("mode", [
    z
        .object({
        mode: z.literal("DETERMINISTIC"),
        adapterId: z.string().min(1).max(160),
        adapterVersion: z.string().min(1).max(100),
        adapterHashSha256: sha256Schema,
        externalCallsAllowed: z.literal(false),
    })
        .strict(),
    z
        .object({
        mode: z.literal("BLOCKED"),
        gate: z.enum([
            "PAID_PROVIDER_DISABLED",
            "HUMAN_APPROVAL_REQUIRED",
            "DETERMINISTIC_ADAPTER_UNAVAILABLE",
        ]),
        configuredExecutor: z.enum(["SONNET_5", "IMAGEGEN", "DETERMINISTIC", "HUMAN"]),
        reason: z.string().min(1).max(1_000),
        externalCallsAllowed: z.literal(false),
    })
        .strict(),
]);
export const productBookProductionJobV1Schema = z
    .object({
    version: z.literal(1),
    jobId: ulidSchema,
    runId: ulidSchema,
    rootJobId: ulidSchema,
    retryOfJobId: ulidSchema.optional(),
    attempt: z.number().int().nonnegative().max(3),
    bookId: ulidSchema,
    productionFamily: productBookProductionFamilySchema,
    stage: productBookProductionStageSchema,
    state: productBookProductionJobStateSchema,
    expectedBookRevision: z.number().int().nonnegative(),
    expectedProductionRevision: z.number().int().nonnegative(),
    sourceBinding: bookGenerationSourceBindingV1Schema,
    requestedBy: ulidSchema,
    execution: productBookProductionExecutionV1Schema,
    failureReason: z.string().min(1).max(1_000).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    terminalAt: z.string().datetime().optional(),
})
    .strict()
    .superRefine((job, context) => {
    const terminal = ["SUCCEEDED", "NEEDS_ATTENTION", "BLOCKED", "CANCELLED"].includes(job.state);
    if (terminal !== Boolean(job.terminalAt))
        context.addIssue({
            code: "custom",
            path: ["terminalAt"],
            message: "Terminal jobs require terminalAt and non-terminal jobs must omit it.",
        });
    const blockedState = job.state === "BLOCKED";
    const blockedExecution = job.execution.mode === "BLOCKED";
    if (blockedState !== blockedExecution)
        context.addIssue({
            code: "custom",
            path: ["execution", "mode"],
            message: "BLOCKED state and BLOCKED execution mode must be equivalent.",
        });
    if ((blockedState || job.state === "NEEDS_ATTENTION") && !job.failureReason)
        context.addIssue({
            code: "custom",
            path: ["failureReason"],
            message: "Blocked and needs-attention jobs require an explicit reason.",
        });
    if (job.execution.mode === "DETERMINISTIC" &&
        ["QUEUED", "RUNNING", "SUCCEEDED"].includes(job.state) &&
        job.failureReason)
        context.addIssue({
            code: "custom",
            path: ["failureReason"],
            message: "Active and successful deterministic jobs cannot carry a blocked reason.",
        });
    if (job.productionFamily === "COMIC_CORE") {
        if (!comicProductionStageV2Schema.safeParse(job.stage).success)
            context.addIssue({
                code: "custom",
                path: ["stage"],
                message: "Comic jobs require a Comic production stage.",
            });
    }
    else if (!recipeProductionStageV1Schema.safeParse(job.stage).success)
        context.addIssue({
            code: "custom",
            path: ["stage"],
            message: "Recipe jobs require a Recipe production stage.",
        });
});
export const productBookProductionBudgetEvidenceV1Schema = z
    .object({
    version: z.literal(1),
    budgetId: z.string().min(1).max(240),
    policyId: z.string().min(1).max(160),
    policyVersion: z.string().min(1).max(100),
    reservationIds: z.array(ulidSchema).max(100),
    actualCostMicros: z.number().int().nonnegative(),
    costEvidenceSource: bookGenerationCostEvidenceSourceSchema,
})
    .strict();
const productBookSourceSnapshotBookV1Schema = z
    .object({
    id: ulidSchema,
    revision: z.number().int().nonnegative(),
    templateId: z.string().min(1).max(100),
    title: z.string().max(200),
    storytellerName: z.string().max(160),
    creativeDirection: z.unknown().nullable(),
})
    .strict();
export const comicBookSourceSnapshotV1Schema = z
    .object({
    schemaVersion: z.literal(1),
    productionFamily: z.literal("COMIC_CORE"),
    book: productBookSourceSnapshotBookV1Schema,
    items: z
        .array(z
        .object({
        id: z.string().min(1).max(160),
        position: z.number().int().nonnegative(),
        revision: z.number().int().nonnegative(),
        approvedRevision: z.number().int().nonnegative().nullable(),
        state: z.string().min(1).max(100),
        title: z.string().max(200),
        prompt: z.string().max(2_000),
        sourceWords: z.string().max(100_000),
        currentDraft: z.string().max(100_000),
    })
        .strict())
        .min(1)
        .max(200),
})
    .strict();
export const recipeBookSourceSnapshotV1Schema = z
    .object({
    schemaVersion: z.literal(1),
    productionFamily: z.literal("RECIPE"),
    book: productBookSourceSnapshotBookV1Schema,
    items: z
        .array(z
        .object({
        id: z.string().min(1).max(160),
        position: z.number().int().nonnegative(),
        revision: z.number().int().nonnegative(),
        approvedRevision: z.number().int().nonnegative().nullable(),
        state: z.string().min(1).max(100),
        content: recipeContentV1Schema,
    })
        .strict())
        .min(1)
        .max(200),
})
    .strict();
export const productBookSourceSnapshotV1Schema = z.discriminatedUnion("productionFamily", [
    comicBookSourceSnapshotV1Schema,
    recipeBookSourceSnapshotV1Schema,
]);
export const productBookSourceLockArtifactV1Schema = z
    .object({
    version: z.literal(1),
    artifactId: ulidSchema,
    runId: ulidSchema,
    bookId: ulidSchema,
    productionFamily: productBookProductionFamilySchema,
    stage: z.literal("SOURCE_LOCK"),
    kind: z.literal("SOURCE_SNAPSHOT"),
    contentVersionId: z.string().startsWith("sha256:").max(200),
    checksumSha256: sha256Schema,
    sourceBinding: bookGenerationSourceBindingV1Schema,
    implementationBinding: z
        .object({
        implementationId: z.string().min(1).max(160),
        implementationVersion: z.string().min(1).max(100),
        implementationHashSha256: sha256Schema,
    })
        .strict(),
    budgetEvidence: productBookProductionBudgetEvidenceV1Schema,
    prerequisiteArtifactIds: z.array(z.string().min(1).max(240)).length(0),
    snapshot: productBookSourceSnapshotV1Schema,
    immutable: z.literal(true),
    createdAt: z.string().datetime(),
})
    .strict()
    .superRefine((artifact, context) => {
    if (artifact.checksumSha256 !== artifact.sourceBinding.sourceHashSha256)
        context.addIssue({
            code: "custom",
            path: ["checksumSha256"],
            message: "The SOURCE_LOCK checksum must equal its exact source binding.",
        });
    if (artifact.contentVersionId !== `sha256:${artifact.checksumSha256}`)
        context.addIssue({
            code: "custom",
            path: ["contentVersionId"],
            message: "The inline content version must be the artifact's content-addressed checksum.",
        });
    if (artifact.productionFamily !== artifact.snapshot.productionFamily)
        context.addIssue({
            code: "custom",
            path: ["snapshot", "productionFamily"],
            message: "The source snapshot must belong to the artifact's production family.",
        });
    if (artifact.budgetEvidence.actualCostMicros !== 0 ||
        artifact.budgetEvidence.costEvidenceSource !== "NO_PROVIDER_COST" ||
        artifact.budgetEvidence.reservationIds.length !== 0)
        context.addIssue({
            code: "custom",
            path: ["budgetEvidence"],
            message: "Deterministic SOURCE_LOCK artifacts cannot claim provider spend.",
        });
});
export const productBookProductionRunV1Schema = z
    .object({
    version: z.literal(1),
    runId: ulidSchema,
    bookId: ulidSchema,
    productionFamily: productBookProductionFamilySchema,
    state: z.enum(["ACTIVE", "COMPLETE", "CANCELLED"]),
    revision: z.number().int().nonnegative(),
    sourceBinding: bookGenerationSourceBindingV1Schema,
    stageGraph: z
        .object({
        graphId: z.string().min(1).max(160),
        graphVersion: z.string().min(1).max(100),
        graphHashSha256: sha256Schema,
    })
        .strict(),
    budget: z
        .object({
        budgetId: z.string().min(1).max(240),
        policyId: z.string().min(1).max(160),
        policyVersion: z.string().min(1).max(100),
    })
        .strict(),
    completedStages: z.array(productBookProductionStageSchema).max(40),
    activeJobId: ulidSchema.optional(),
    blocker: z.string().min(1).max(1_000).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    terminalAt: z.string().datetime().optional(),
})
    .strict()
    .superRefine((run, context) => {
    const terminal = run.state === "COMPLETE" || run.state === "CANCELLED";
    if (terminal !== Boolean(run.terminalAt))
        context.addIssue({
            code: "custom",
            path: ["terminalAt"],
            message: "Terminal runs require terminalAt and active runs must omit it.",
        });
    if (run.activeJobId && run.state !== "ACTIVE")
        context.addIssue({
            code: "custom",
            path: ["activeJobId"],
            message: "Only an active production run may reference active work.",
        });
    if (new Set(run.completedStages).size !== run.completedStages.length)
        context.addIssue({
            code: "custom",
            path: ["completedStages"],
            message: "Completed production stages must be unique.",
        });
});
export const productBookProductionStageStateV1Schema = z
    .object({
    stage: productBookProductionStageSchema,
    executor: z.enum(["SONNET_5", "IMAGEGEN", "DETERMINISTIC", "HUMAN"]),
    prerequisites: z.array(productBookProductionStageSchema).max(10),
    status: z.enum([
        "COMPLETE",
        "EXECUTABLE",
        "BLOCKED_PREREQUISITE",
        "BLOCKED_PROVIDER",
        "BLOCKED_HUMAN",
        "BLOCKED_ADAPTER",
    ]),
    reason: z.string().min(1).max(1_000).optional(),
})
    .strict();
export const productBookProductionViewV1Schema = z
    .object({
    version: z.literal(1),
    run: productBookProductionRunV1Schema.nullable(),
    jobs: z.array(productBookProductionJobV1Schema).max(100),
    artifacts: z.array(productBookSourceLockArtifactV1Schema).max(100),
    budget: bookGenerationBudgetV1Schema.nullable(),
    stageStates: z.array(productBookProductionStageStateV1Schema).min(1).max(40),
    eligibleStages: z.array(productBookProductionStageSchema).max(40),
    executableStages: z.array(productBookProductionStageSchema).max(40),
    blocker: z.string().min(1).max(1_000).optional(),
})
    .strict();
//# sourceMappingURL=product-book-production.js.map